/*
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import {MutableRefObject, useCallback, useEffect, useRef, useState} from 'react';
import {hashQueryKey, QueryClient, useQueryClient} from 'react-query';
import isEqual from 'lodash.isequal';
import {error} from '@ion/core/src/logger/logger';
import {GenericObject} from '@ion/core/src/types/entities';
import {safeJSONParse, safeJSONStringify} from '@ion/core/src/utils/JSONUtils';
import {sortByKey} from '@ion/core/src/utils/ObjectUtil';
import QueryKey, {createKey} from '../config/QueryKeys';
import {getStaleTime} from '../utils/helpers';
import usePagination, {UsePaginationResult} from './usePagination';
import usePaginationPrefetch from './usePaginationPrefetch';

// defines the result type of usePaginatedQueryCache hook...
export interface UsePaginatedQueryCacheResult {
  data: MutableRefObject<any>;
  currentCallback: MutableRefObject<any>;
  currentQueryKey: MutableRefObject<any>;
  currentParams: MutableRefObject<any>;
  fetching: boolean;
  paging: UsePaginationResult;
  loadItems: Function;
  refresh: boolean;
  onRefresh: Function;
  refreshCurrentBlock: Function;
  createQuerykey: Function;
  queryClient: QueryClient;
  initialized: boolean;
};

// defines the props type of usePaginatedQueryCache hook...
export interface UsePaginatedQueryCacheProps {
  api: Function;
  baseKey: QueryKey;
  parseKey?: string;
  pageKey?: string;
  pageSizeKey?: string;
  initialPage?: number;
  initialPageSize?: number;
  parser?: Function;
  staleTime?: number;
  refetchInterval?: number;
  retry?: boolean | number;
  apiParams?: any;
  refetchOnMount?: boolean;
  needStartRow?: boolean;
  gridApi?: GenericObject;
  enabled?: boolean;
  enabledPrefetch?: boolean;
  purgeUpdateOnDataUpdate?: boolean;
  comparator?: (response: any, cached: any) => boolean;
  // given a refreshed block that changed, may apply it to the grid in place
  // and return `true`, which skips the purge (and the rebuild of every row)
  // that would otherwise follow ...
  onBlockUpdate?: (response: any, cached: any) => boolean;
  // fetches a neighbouring page for prefetching - `api` by default ...
  prefetchApi?: Function;
  // marks every other cached page of this grid stale on each block refresh,
  // without fetching it, so a page opened later is fetched fresh ...
  invalidateNeighboursOnRefresh?: boolean;
};

// default function to parse the CTC paginated data...
const parseCTCPaginatedData = (response: GenericObject, parseKey: string) => {
  return {items: response[parseKey] || [], totalCount: response.totalCount ?? 0};
};

/**
 * The usePaginatedQueryCache hook is responsible for managing the paginated
 * post search with CTC, using React-Query. The hook initially fetches the current page
 * and the next page, and pre-loads subsequent pages as the user navigates to the
 * next page (pre-fetching one page in advance to enhance the user experience).
 *
 * ```typescript
 *
 *  import {usePaginatedQueryCache} from '@ion/query/src/hooks';
 *
 *  const {loadItems, refresh} = usePaginatedQueryCache({
 *   api             : getCrashEvents,
 *   baseKey         : QueryKey.GET_CRASH_EVENTS,
 *   parseKey        : 'crashEvents',
 *   initialPageSize : 20
 *  });
 *
 * ```
 */
export const usePaginatedQueryCache = ({
  api,
  gridApi,
  baseKey,
  parseKey,
  apiParams,
  comparator,
  refetchInterval,
  retry = undefined,
  initialPage = 1,
  initialPageSize = 20,
  pageKey = 'pageId',
  needStartRow = false,
  enabled = true,
  enabledPrefetch = true,
  refetchOnMount = false,
  pageSizeKey = 'pageSize',
  parser = parseCTCPaginatedData,
  purgeUpdateOnDataUpdate = true,
  onBlockUpdate,
  prefetchApi,
  invalidateNeighboursOnRefresh = false,
  staleTime = getStaleTime(5, false, false) as number
}: UsePaginatedQueryCacheProps) => {
  const currentQueryKey               = useRef(null);
  const [initialized, setInitialized] = useState(false);
  const currentParams                 = useRef<any>({});
  const currentCallback               = useRef((param) => {});
  const data                          = useRef(null);
  const interval                      = useRef(null);
  const isPurgeUpdate                 = useRef(false);
  const [fetching, setFetching]       = useState(enabled);
  const [refresh, setRefresh]         = useState(false);
  const queryClient                   = useQueryClient();
  const paging: UsePaginationResult   = usePagination(initialPage, initialPageSize);

  const triggerRefresh = useCallback(() => {
    if (!enabled) return;
    setRefresh(true);
    const timeout = setTimeout(() => {
      setRefresh(false);
      clearTimeout(timeout);
    }, 500);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setRefresh(false);
      setFetching(false);
      return;
    }
    triggerRefresh();
  }, [apiParams, enabled, triggerRefresh]);

  const refreshCurrentBlock = useCallback(async() => {
    if (!enabled || !currentQueryKey.current) return;
    const cached = queryClient.getQueryData(currentQueryKey.current);
    queryClient.invalidateQueries(currentQueryKey.current, {exact: true});
    const response = await queryClient.fetchQuery({
      queryKey : currentQueryKey.current,
      queryFn  : async() => await api(currentParams.current),
      retry,
      staleTime
    });
    const hasUpdate = comparator ? comparator(response, cached) : !isEqual(response, cached);
    if (invalidateNeighboursOnRefresh) {
      // compared by hash - react-query keeps a string key as `[key]` ...
      const currentHash = hashQueryKey(currentQueryKey.current);
      queryClient.invalidateQueries({
        predicate : query => query.queryHash !== currentHash &&
          safeJSONParse(query.queryKey as string)?.[0] === baseKey,
        refetchActive   : false,
        refetchInactive : false
      });
    }
    if (hasUpdate && onBlockUpdate?.(response, cached)) {
      const parsed = parser(response, parseKey);
      paging.setTotal(parsed.totalCount ?? 0, true);
    } else if (hasUpdate) {
      if (purgeUpdateOnDataUpdate) {
        isPurgeUpdate.current = true;
        gridApi?.purgeInfiniteCache?.();
      } else {
        const parsed = parser(response, parseKey);
        paging.setTotal(parsed.totalCount ?? 0, true);
        currentCallback.current?.(parsed);
      }
    }
    return response;
  }, [enabled, gridApi, api, comparator, parseKey, onBlockUpdate, invalidateNeighboursOnRefresh]);

  const scheduleDataRefresh = useCallback(() => {
    clearInterval(interval.current);
    interval.current = setInterval(async() => {
      await refreshCurrentBlock();
    }, refetchInterval);
  }, [refreshCurrentBlock]);

  useEffect(() => {
    // If refetchInterval is provided, set up an interval
    // to refetch data periodically
    if (enabled && !refresh && gridApi && refetchInterval) {
      scheduleDataRefresh();
    }
    // Cleanup interval on component unmount
    return () => clearInterval(interval.current);
  }, [enabled, gridApi, refresh]);

  usePaginationPrefetch({
    fetching,
    resolver : (page: number) => (prefetchApi ?? api)({
      ...currentParams.current,
      [pageKey]     : page,
      [pageSizeKey] : currentParams.current[pageSizeKey],
      ...(needStartRow ? {startRow: (page - 1) * currentParams.current[pageSizeKey]} : {})
    }),
    // Determines if there are more pages available to fetch
    hasNext     : paging.hasNext,
    hasPrevious : paging.hasPrevious,
    page        : paging.page,
    // Generates a unique key for the next page's query
    key         : safeJSONStringify(sortByKey(createKey(baseKey, {
      ...currentParams.current,
      [pageKey] : currentParams.current[pageKey] + 1,
      ...(needStartRow ? {startRow: currentParams.current[pageKey] * currentParams.current[pageSizeKey]} : {})
    }))) as any,
    // Generates a unique key for the previous page's query
    previousPageKey : safeJSONStringify(sortByKey(createKey(baseKey, {
      ...currentParams.current,
      [pageKey] : currentParams.current[pageKey] - 1,
      ...(needStartRow ? {startRow: (currentParams.current[pageKey] - 2) * currentParams.current[pageSizeKey]} : {})
    }))) as any,
    enabled : enabled && enabledPrefetch,
    retry,
    staleTime
  });

  const createQuerykey = useCallback((params) => (
    safeJSONStringify(sortByKey(createKey(baseKey, {
      ...params,
      [pageKey] : params[pageKey],
      ...(needStartRow ? {startRow: (params[pageKey] - 1) * currentParams.current[pageSizeKey]} : {})
    })))
  ), [baseKey, pageKey, needStartRow, pageSizeKey]);

  const loadItems = useCallback(async({callback, selectedAssetKey, ...params}) => {
    if (!enabled) {
      setFetching(false);
      return null;
    }
    setFetching(true);

    // Set the current page and page size in the pagination state
    paging.setPage(params[pageKey]);
    paging.setPageSize(params[pageSizeKey]);

    // Update the current parameters & callback references...
    currentParams.current   = params;
    currentCallback.current = callback;

    // Create a unique key for the current query using the provided baseKey and params
    const key = createQuerykey(params);

    const processData = response => {
      if (response) {
        // Parse the response data using the provided parser function
        const parsed = parser(response, parseKey);

        // Update the total count in the pagination state
        paging.setTotal(parsed.totalCount ?? 0, true);

        // Execute the callback function if provided
        // i,e the callback function passed from IonGrid
        callback?.(parsed, {
          response,
          queryClient,
          key,
          params,
          createQuerykey
        });
      }
    };

    let cached;
    const isSameKey = !selectedAssetKey || JSON.stringify(selectedAssetKey) === JSON.stringify(key);
    // Check if there's cached data for the current query key
    if (isSameKey) {
      cached = queryClient.getQueryData(key);
    } else {
      queryClient.removeQueries({queryKey: [key]});
    }

    try {
      // Update the current query key reference
      currentQueryKey.current = key;

      if (!isPurgeUpdate.current && refetchOnMount && cached) {
        processData(cached);
        queryClient.invalidateQueries(currentQueryKey.current, {exact: true});
      }
      isPurgeUpdate.current = false;

      // Fetch data using react-query's fetchQuery method
      const response = await queryClient.fetchQuery({
        queryKey : key,
        queryFn  : async() => await api(params),
        retry,
        staleTime
      });

      // Store the response data
      data.current = response || cached || {};

      processData(data.current);

      setFetching(false);
      !initialized && setInitialized(true);
      return data.current;
    } catch (err) {
      // call the callback with cached data when there is error
      // occured and if the data is already cached
      processData(cached || {});
      // If an error occurs,set fetching state to false
      setFetching(false);
      return error(err);
    }
  }, [
    initialized,
    gridApi,
    paging,
    staleTime,
    baseKey,
    pageKey,
    pageSizeKey,
    api,
    parser,
    apiParams,
    refetchOnMount,
    refetchInterval,
    retry,
    enabled
  ]);

  const onRefresh = useCallback((shouldToggleRefreshState: boolean = true) => {
    const queryCache  = queryClient.getQueryCache();
    const queries     = queryCache.findAll();
    // Filter the queries to find only those that match the baseKey
    const userQueries = queries.filter(
      query => safeJSONParse(query.queryKey as string)[0]?.startsWith(baseKey)
    );
    // Remove each query that matches the baseKey from the query cache
    userQueries.forEach(query => {
      queryClient.removeQueries(query.queryKey);
    });
    if (shouldToggleRefreshState) {
      triggerRefresh();
    }
  }, [baseKey]);

  return {
    refreshCurrentBlock,
    currentQueryKey,
    currentCallback,
    data,
    fetching,
    paging,
    loadItems,
    refresh,
    onRefresh,
    queryClient,
    initialized,
    currentParams,
    createQuerykey
  } as UsePaginatedQueryCacheResult;
};

export default usePaginatedQueryCache;
